import React, { useEffect, useState } from "react";
import "./OneWayAttendeeRegistration.css";
import forge from "node-forge";
import { fetchOneWayAttendeeRegistrationSecrets } from "./APIService";
import config from "../configurations/config";
import classnames from 'classnames';

const OneWayAttendeeRegistrationForm = () => {
  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState(null);
  const [secretsData, setSecretsData] = useState(null);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    const fetchSecretData = async () => {
      try {
        const secretName = "oeintegration/onewayattendeeregistration/auth";
        const response = await fetchOneWayAttendeeRegistrationSecrets(secretName);
        setSecretsData(response);
      } catch (error) {
        setError("Error fetching data: " + error.message);
      }
    };
    fetchSecretData();
  }, []);

  const handleChange = ({ target: { name, value } }) => {
    setFormData((prevData) => ({ ...prevData, [name]: value }));
    setFieldErrors((prevErrors) => ({ ...prevErrors, [name]: null }));
    setSubmissionStatus(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const nonEmptyData = getNonEmptyData({
      ...formData,
      integrationAccountId: secretsData?.INTEGRATION_ACCOUNT_ID,
      integratorId: secretsData?.INTEGRATOR_ID,
    });

    const jsonData = JSON.stringify(nonEmptyData);
    const signature = createSignature(jsonData, secretsData?.PRIVATE_KEY);

    try {
      const response = await submitData(jsonData, signature);
      handleResponse(response);
      setSubmissionStatus("Success! Your data has been submitted.");
    } catch (error) {
        throw error
    } finally {
      setLoading(false);
    }
  };

  const getNonEmptyData = (data) => {
    return Object.fromEntries(Object.entries(data).filter(([_, value]) => value !== ""));
  };

  const createSignature = (data, privateKeyBase64) => {
    const privateKey = getPrivateKeyFromBase64(privateKeyBase64);
    const md = forge.md.sha256.create();
    md.update(data, "utf8");
    return forge.util.encode64(privateKey.sign(md));
  };

  const submitData = async (jsonData, signature) => {
    const subDomain = config.apiGateway.URL.match(/\.(.*?)\-/)[1];
    const registrationURL = subDomain === 'dev' || subDomain === 'stage' ? `https://passport-api.${subDomain}-open-exchange.net/attendee/one-way-register` : `https://passport-api.open-exchange.net/attendee/one-way-register`
    const response = await fetch(registrationURL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Api-Signature": signature,
        "X-Api-KeyId": secretsData?.X_API_KEY_ID,
      },
      body: jsonData,
    });

    if (!response.ok) {
      const data = await response.json();
      handleFetchErrors(response, data);
    }
    return response;
  };

  const handleFetchErrors = (response, data) => {
    if (response.status === 400) {
      const errors = data.fields.reduce((acc, field) => {
        acc[field.name] = field.message;
        return acc;
      }, {});
      setFieldErrors(errors);
      setSubmissionStatus(null);
    } else {
      console.error(`Error: ${response.status} ${data.message}`);
      setSubmissionStatus(data.message || 'Error! There was an issue submitting your data');
      throw new Error(data.message);
    }
  };

  const handleResponse = (data) => {
    if (data.status === 200) {
      const locationHeader = data.url;
      const urlParams = new URLSearchParams(new URL(locationHeader).search);
      const displayUrl = urlParams.get('redirectUrl');

      if (displayUrl) {
        window.location.href = displayUrl;
      } else if (locationHeader) {
        window.location.href = locationHeader;
      } else {
        throw new Error("302 response but no Location header found.");
      }
    } else {
      throw new Error("Network response was not ok");
    }
  };

  return (
    <div className="form-container">
      <h1>Attendee Registration Form</h1>
      <form onSubmit={handleSubmit}>
        {Object.entries(formData).map(([key, value]) => (
          <FormGroup
            key={key}
            name={key}
            value={value}
            error={fieldErrors[key]}
            onChange={handleChange}
          />
        ))}
        <div className="form-group">
          <button type="submit" className="submit-button" disabled={loading && error}>
            {loading ? "Submitting..." : "Submit"}
          </button>
        </div>
      </form>
      {submissionStatus && <p className={classnames('submission-status', {'success': submissionStatus.includes('Success!')})}>{submissionStatus}</p>}
    </div>
  );
};

const FormGroup = ({ name, value, error, onChange }) => (
  <div className="form-group">
    <label htmlFor={name}>{formatLabel(name)}</label>
    <div className="form-field">
      <input
        id={name}
        name={name}
        type={getInputType(name)}
        value={value || ""}
        onChange={onChange}
        autoComplete="off"
      />
      {error && <span className="error-message">{error}</span>}
    </div>
  </div>
);

const getInputType = (name) => {
  if (name.includes("Email")) return "email";
  if (name.includes("Phone")) return "tel";
  return "text";
};

const getPrivateKeyFromBase64 = (privateKeyBase64) => {
  const privateKeyPem =
    `-----BEGIN PRIVATE KEY-----\n` +
    privateKeyBase64.match(/.{1,64}/g)?.join("\n") +
    `\n-----END PRIVATE KEY-----`;
  return forge.pki.privateKeyFromPem(privateKeyPem);
};

const initialFormData = {
  firstName: "",
  lastName: "",
  email: "",
  organizationName: "",
  organizationType: "",
  salutation: "",
  phone: "",
  title: "",
  assistantEmail: "",
  externalEventId: "",
  externalMeetingId: "",
  externalAttendeeId: "",
  eventUrl: null,
};

const formatLabel = (key) => {
  return key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase());
};

export default OneWayAttendeeRegistrationForm;
